import { Autocomplete } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useTransactions } from "api/transactions";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { MemberListItem } from "components/Tooltips/ClaimRewardModal";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { OLButton } from "OLButton";
import React, { useState } from "react";
import { ListResponse } from "types/listing";
import { BackendMemberModel } from "types/members";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    modalButton: {
      marginTop: theme.spacing(12),
    },
    SelectInput: {
      maxWidth: "100%",
      width: 406,
      "& + &": {
        marginTop: theme.spacing(10),
      },
    },
  }),
);

export const MatchTransactionModal = ({
  open,
  close,
  selectedModal,
  assignTransaction,
  setSearchedMemberValue,
  searchedMembers,
}: {
  open: boolean;
  close: (val: boolean) => void;
  selectedModal: {
    transaction: string;
    mode: number;
  };
  assignTransaction: any;
  setSearchedMemberValue: React.Dispatch<React.SetStateAction<string>>;
  searchedMembers: ListResponse;
}) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { useInfiniteTransactionsList } = useTransactions();
  const [transactionSearch, setTransactionSearch] = useState("");

  const {
    data: infiniteTransactionsList,
    size: transactionsSize,
    setSize: setTransactionsSize,
    isValidating,
  } = useInfiniteTransactionsList({
    filters: transactionSearch
      ? [
          {
            condition: "like",
            query: "header:documentNumber",
            value: transactionSearch,
          },
        ]
      : undefined,
  });

  const {
    infiniteData: transactionsList,
    anyElementsToFetch: anyTransactionsToFetch,
  } = useManageInfiniteScroll(infiniteTransactionsList || []);

  const getMemberLabel = (option: BackendMemberModel) => {
    if (option.email) {
      return option.email;
    } else if (option.loyaltyCardNumber) {
      return `${message({ id: "loyaltyCardNumber" })}: ${
        option.loyaltyCardNumber
      }`;
    } else if (option.phone) {
      return `${message({ id: "phoneNumber" })}: ${option.phone}`;
    } else if (option.customerId) {
      return `${message({ id: "customerId" })}: ${option.customerId}`;
    } else {
      return "";
    }
  };

  return (
    <OLModal
      header={message({ id: "matchTransaction" })}
      onClose={() => close(false)}
      open={open}
    >
      <Formik
        initialValues={{
          customerId: "",
          transactionDocumentNumber: selectedModal.transaction || "",
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const response = await assignTransaction(values);
            if (response?.data?.customerId) {
              close(false);
              notify(message({ id: "transactionMatched" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, setFieldValue, isSubmitting }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Autocomplete
                className={classes.SelectInput}
                getOptionLabel={(member: BackendMemberModel) =>
                  getMemberLabel(member)
                }
                onBlur={(event) => {
                  event && setSearchedMemberValue("");
                }}
                onChange={(_, newInputValue) => {
                  if (newInputValue)
                    setFieldValue("customerId", newInputValue["customerId"]);
                }}
                onInputChange={(e: any) =>
                  setSearchedMemberValue(e.target["value"])
                }
                popupIcon={<ChevronDownIcon />}
                options={searchedMembers ? searchedMembers.items : []}
                renderInput={(params) => (
                  <TextInput
                    {...params}
                    className={classes.SelectInput}
                    InputLabelProps={{ shrink: true }}
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "selectMember" })}
                      />
                    }
                    name="customerId"
                    placeholder={message({
                      id: "selectMemberInfo",
                    })}
                    data-test-id="transaction-match-with-member-select-member-select"
                  />
                )}
                renderOption={(props, member: BackendMemberModel) => (
                  <MemberListItem
                    props={{
                      ...props,
                      "data-hj-suppress": true,
                      "data-test-id": "member",
                    }}
                    member={member}
                  />
                )}
                filterOptions={(options: BackendMemberModel[]) => options}
              />
              {selectedModal.transaction ? null : (
                <>
                  <SelectInput
                    className={classes.SelectInput}
                    displaySearch
                    data-test-id="transaction-match-with-member-document-number-select"
                    items={transactionsList.map((transaction) => ({
                      label: transaction.header?.documentNumber || "",
                      id: transaction.header?.documentNumber || "",
                    }))}
                    asyncSearch={(searchedQuery) =>
                      setTransactionSearch(searchedQuery)
                    }
                    label={
                      <RequiredFieldLabel
                        text={message({
                          id: "documentNumber",
                        })}
                      />
                    }
                    name="transactionDocumentNumber"
                    placeholder={message({
                      id: "findByDocumentNumber",
                    })}
                    loadMoreElements={() =>
                      !isValidating && setTransactionsSize(transactionsSize + 1)
                    }
                    anyElementsToFetch={anyTransactionsToFetch}
                  />
                </>
              )}
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="transaction-match-with-member-match-button"
              >
                {message({ id: "match" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
